import React from 'react';
import { M365User, GroupMembership, UserLicense } from '../../types';
import { getGroupRemovalBlocker } from '../../services/graphService';

interface UserCardProps {
  user: M365User;
  groups: GroupMembership[];
  licenses: UserLicense[];
}

/**
 * Fiche détaillée de l'utilisateur sélectionné pour l'offboarding
 * Affiche toutes les infos pertinentes récupérées via Graph API
 */
const UserCard: React.FC<UserCardProps> = ({ user, groups, licenses }) => {
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
      <div className="flex items-start gap-4">
        {/* Avatar avec initiales */}
        <div className="w-14 h-14 bg-ms-blue rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-lg font-semibold">
            {user.displayName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-ms-gray-800">{user.displayName}</h3>
            <span
              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                user.accountEnabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {user.accountEnabled ? 'Actif' : 'Désactivé'}
            </span>
            {user.onPremisesSyncEnabled && (
              <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-amber-100 text-amber-700" title="Synchronisé depuis l'Active Directory local — certaines opérations doivent être faites on-premises">
                AD synchronisé
              </span>
            )}
          </div>
          <p className="text-sm text-ms-gray-500">{user.mail || user.userPrincipalName}</p>
        </div>
      </div>

      {/* Informations détaillées */}
      <div className="grid grid-cols-2 gap-4 mt-5">
        <InfoField label="Poste" value={user.jobTitle} />
        <InfoField label="Département" value={user.department} />
        <InfoField label="Société" value={user.companyName} />
        <InfoField
          label="Manager"
          value={user.manager ? `${user.manager.displayName} (${user.manager.mail})` : null}
        />
        <InfoField label="UPN" value={user.userPrincipalName} />
        <InfoField label="Dernière connexion" value={formatDate(user.lastSignInDateTime)} />
      </div>

      {/* Licences — ce qui sera effectivement libéré */}
      <div className="mt-5">
        <p className="text-sm font-medium text-ms-gray-700 mb-2">
          Licences ({licenses.length})
        </p>
        {licenses.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {licenses.map((license) => (
              <span
                key={license.skuId}
                className={`px-2 py-1 text-xs rounded-md ${
                  license.assignedByGroup
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-ms-gray-100 text-ms-gray-600'
                }`}
                title={
                  license.assignedByGroup
                    ? "Licence héritée d'un groupe : elle se retire en quittant le groupe, pas directement"
                    : 'Licence attribuée directement : libérée par l\'action « Retirer les licences »'
                }
              >
                {license.skuPartNumber}
                {license.assignedByGroup && ' (via groupe)'}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ms-gray-400">Aucune licence</p>
        )}
      </div>

      {/* Groupes — les non modifiables via Graph sont signalés d'emblée */}
      <div className="mt-5">
        <p className="text-sm font-medium text-ms-gray-700 mb-2">
          Groupes ({groups.length})
        </p>
        {groups.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {groups.map((group) => {
              const blocker = getGroupRemovalBlocker(group);
              return (
                <span
                  key={group.id}
                  className={`px-2 py-1 text-xs rounded-md ${
                    blocker
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-ms-gray-100 text-ms-gray-600'
                  }`}
                  title={blocker ? `Retrait manuel nécessaire : ${blocker}` : undefined}
                >
                  {group.displayName}
                  {blocker && ' ⚠'}
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-ms-gray-400">Aucun groupe</p>
        )}
      </div>
    </div>
  );
};

/** Champ d'information avec label */
const InfoField: React.FC<{ label: string; value: string | null | undefined }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs text-ms-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm text-ms-gray-700 mt-0.5">{value || '—'}</p>
  </div>
);

export default UserCard;
